import type { KeyboardEvent, MouseEvent } from 'react';
import { Sparkles } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import type { CatalogLibraryCard } from '@/features/catalog/types';

interface CatalogWorldCardProps {
  card: CatalogLibraryCard;
  /** Pre-formatted DD.MM.YYYY of the latest unlock (caller owns formatting). */
  unlockedDate: string;
  onOpen: () => void;
}

/**
 * Library row for an owned Wissenswelt (Block C2). Mirrors SeriesLibraryCard
 * dimensions/structure so it interleaves seamlessly with story + series rows:
 * a "Wissenswelt" badge, the world title, a progress bar with "X von Y Folgen"
 * (unlocked / produced), and ONE "Welt öffnen" action → the world-detail view.
 * Cover images land in a later block; until then the leading tile is an icon.
 */
export function CatalogWorldCard({ card, unlockedDate, onOpen }: CatalogWorldCardProps) {
  const S = STRINGS.catalog.library;
  const total = card.totalCount;
  const done = card.unlockedCount;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };
  const stopAndOpen = (e: MouseEvent) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className="flex w-full flex-row items-stretch overflow-hidden rounded-lg bg-blue-bg text-left transition-colors hover:bg-blue-bg/70 focus-visible:outline-2 focus-visible:outline-dream-navy/50"
    >
      <div className="mt-3 ml-3 flex h-[40px] w-[40px] shrink-0 items-center justify-center self-start rounded bg-dream-navy/10 text-dream-navy">
        <Sparkles aria-hidden className="h-5 w-5" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3 text-dream-navy">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1 text-xxs leading-tight text-dream-navy/60">
            {unlockedDate && <span>{unlockedDate}</span>}
            <span>·</span>
            <span>{S.parentWorld}</span>
          </div>
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-dream-navy/10 px-2 py-0.5 text-xxs font-semibold text-dream-navy">
            <Sparkles aria-hidden className="h-3 w-3" />
            {S.badge}
          </span>
        </div>

        <p className="line-clamp-2 text-sm font-semibold leading-tight">{card.title}</p>

        <div className="flex flex-col gap-1">
          <div className="h-[6px] w-full overflow-hidden rounded-full bg-dream-navy/10">
            <div className="h-full rounded-full bg-dream-gold" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xxs text-dream-navy/70">
            {S.progress.replace('{unlocked}', String(done)).replace('{total}', String(total))}
          </span>
        </div>

        <button
          type="button"
          onClick={stopAndOpen}
          className="mt-1 h-9 w-full rounded-lg bg-dream-navy text-sm font-medium text-dream-white transition hover:bg-dream-navy/90"
        >
          {S.open}
        </button>
      </div>
    </div>
  );
}
